import { useEffect, useState, type FormEvent } from "react";

import { submitAccountOnboarding } from "@/api/onboarding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { PrefillAccountInfo } from "@/types/onboarding";

// Matches the backend's OnboardingAccountPayload.password (min_length=8).
export const MIN_PASSWORD_LENGTH = 8;

/** Entered values kept by the flow while moving between steps (never the password). */
export type AccountDraft = {
  email: string;
  firstName: string;
  lastName: string;
  role: "artist" | "manager";
  agreed: boolean;
};

type Props = {
  defaultEmail: string;
  prefill?: PrefillAccountInfo;
  draft?: AccountDraft | null;
  onDraftChange?: (draft: AccountDraft) => void;
  /** True when revisiting after the account was saved; the password may then be left blank. */
  passwordAlreadySet?: boolean;
  onDone: () => Promise<void> | void;
};

/** Step 1: the minimum needed to own an account. No org, billing or integrations. */
export default function AccountBasicsStep({
  defaultEmail,
  prefill,
  draft,
  onDraftChange,
  passwordAlreadySet = false,
  onDone,
}: Props) {
  const [email, setEmail] = useState(draft?.email ?? prefill?.email ?? defaultEmail);
  const [firstName, setFirstName] = useState(draft?.firstName ?? prefill?.first_name ?? "");
  const [lastName, setLastName] = useState(draft?.lastName ?? prefill?.last_name ?? "");
  const [role, setRole] = useState<"artist" | "manager">(draft?.role ?? "artist");
  const [agreed, setAgreed] = useState(draft?.agreed ?? false);
  // Signup is email-only with a placeholder password; this sets the real one.
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const passwordTooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;
  const passwordsMismatch = confirmPassword.length > 0 && password !== confirmPassword;
  const keepingPassword = passwordAlreadySet && password.length === 0 && confirmPassword.length === 0;
  const passwordValid =
    keepingPassword || (password.length >= MIN_PASSWORD_LENGTH && password === confirmPassword);

  useEffect(() => {
    onDraftChange?.({ email, firstName, lastName, role, agreed });
  }, [email, firstName, lastName, role, agreed, onDraftChange]);
  const ready = Boolean(email.trim() && firstName.trim() && lastName.trim() && agreed && passwordValid);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || saving) return;
    setSaving(true);
    setError(null);
    try {
      await submitAccountOnboarding({
        email: email.trim(),
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        user_type: role,
        agree_to_terms: true,
        ...(keepingPassword ? {} : { password }),
      });
      await onDone();
    } catch {
      setError("We couldn't save your account. Please check your details and try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit} aria-label="Account">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Welcome to ArtistAscend</h2>
        <p className="text-sm text-muted-foreground">A few basics and you'll be finding venues in minutes.</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-muted-foreground">
          First name
          <Input className="mt-1" value={firstName} onChange={(e) => setFirstName(e.target.value)} aria-label="First name" />
        </label>
        <label className="text-sm text-muted-foreground">
          Last name
          <Input className="mt-1" value={lastName} onChange={(e) => setLastName(e.target.value)} aria-label="Last name" />
        </label>
      </div>
      <label className="block text-sm text-muted-foreground">
        Email
        <Input className="mt-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Email" />
      </label>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-muted-foreground">
          Password
          <Input
            className="mt-1"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-label="Password"
            aria-invalid={passwordTooShort || undefined}
            aria-describedby="password-help"
          />
        </label>
        <label className="text-sm text-muted-foreground">
          Confirm password
          <Input
            className="mt-1"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            aria-label="Confirm password"
            aria-invalid={passwordsMismatch || undefined}
          />
        </label>
      </div>
      <div id="password-help" className="space-y-1 text-xs">
        {passwordTooShort ? (
          <p role="alert" className="text-destructive">
            Password must be at least {MIN_PASSWORD_LENGTH} characters.
          </p>
        ) : (
          <p className="text-muted-foreground">
            {passwordAlreadySet
              ? "Your password is already set. Leave these blank to keep it."
              : `You'll use this with your email to sign in. At least ${MIN_PASSWORD_LENGTH} characters.`}
          </p>
        )}
        {passwordsMismatch && (
          <p role="alert" className="text-destructive">
            Passwords do not match.
          </p>
        )}
      </div>
      <fieldset className="space-y-2">
        <legend className="text-sm text-muted-foreground">I am…</legend>
        <div className="flex flex-wrap gap-2">
          {(
            [
              ["artist", "An artist or band"],
              ["manager", "A manager or agent"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              onClick={() => setRole(value)}
              aria-pressed={role === value}
              className={`rounded-full border px-4 py-1.5 text-sm transition ${
                role === value ? "border-primary bg-primary/15 text-foreground" : "border-border text-muted-foreground"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </fieldset>
      <label className="flex items-start gap-2 text-sm text-muted-foreground">
        <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} aria-label="Agree to terms" className="mt-1" />
        <span>I agree to the ArtistAscend terms of service and privacy policy.</span>
      </label>
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <div className="flex justify-end">
        <Button type="submit" disabled={!ready || saving}>
          {saving ? "Saving…" : "Continue"}
        </Button>
      </div>
    </form>
  );
}
